import React, { useMemo, useRef } from 'react'
import { HStack, VStack } from '@musinsa/mwms-ds'
import styled from 'styled-components'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import GridTable from '@/components/table/GridTable'
import OutboundWaitingByTimeSlotPanel from './OutboundWaitingByTimeSlotPanel'
import SummaryPanel from './SummaryPanel'
import { OrderItem, ExpectedAllocationItems, GetOrdersReq, B2CUrlParams } from '@/services/outbound/outboundWaitingStatus/types'
import { GRID_SIZE } from '@/utils/grid/grid'
import { useFormContext } from 'react-hook-form'
import FilterList from '@/components/content/FilterList'
import ShipperSelector from '@/components/system/ShipperSelector'
import MultipleInput from '@/components/content/MultipleInput'
import { useFeatureFlagBoolean } from '@/services/feature/useFeatureQueries'

interface B2CContentProps {
  data: any // API 응답 타입 (ApiPageResponse<OrderItem>)
  expectedAllocationData: ExpectedAllocationItems | undefined
  currentPage: number
  isLoading: boolean
  parsedQs: B2CUrlParams
  filteredWarehouses?: string[]
  onSubmit: (data: any) => void
  onChangePage: (page: number) => Promise<void>
  onFieldChange?: (params: { fieldName: any; value: unknown; isSelectedAll?: boolean }) => void
  onReset?: () => void
}

function B2CContent({
  data,
  expectedAllocationData,
  currentPage,
  isLoading,
  parsedQs,
  filteredWarehouses,
  onSubmit,
  onChangePage,
  onFieldChange,
  onReset,
}: B2CContentProps) {
  const $grid = useRef<AgGridReact>(null)

  const { data: isFulfillmentEnabled } = useFeatureFlagBoolean('fulfillment.optimization.b2c.activeShippers', 'enabled')

  const { setValue, watch, reset, handleSubmit } = useFormContext<GetOrdersReq>()

  const values = watch()

  const columnDefs = useMemo<ColDef<OrderItem>[]>(() => {
    return [
      { field: 'shipperName', headerName: '화주명', width: 94 },
      { field: 'orderStatus', headerName: '주문상태', width: 94 },
      { field: 'orderType', headerName: '주문유형', width: 94 },
      { field: 'transportType', headerName: '배송유형', width: 120 },
      { field: 'orderDateTime', headerName: '주문일', cellDataType: 'dateNday' },
      { field: 'collectedDate', headerName: '주문수집일', cellDataType: 'dateNday' },
      { field: 'orderNumber', headerName: '주문번호' },
      { field: 'orderOptionNumber', headerName: '주문일련번호' },
      { field: 'styleNo', headerName: '스타일넘버' },
      { field: 'optionCode', headerName: '옵션코드', width: 94 },
      { field: 'barcode', headerName: '바코드' },
      { field: 'skuId', headerName: 'SKUID', width: 150 },
      { field: 'goodsOption', headerName: '옵션' },
      { field: 'goodsName', headerName: '상품명' },
      { field: 'orderQuantity', headerName: '주문수량', width: 94 },
      { field: 'gift', headerName: '사은품' },
    ]
  }, [])


  const filter = (
    <FilterList
      isLoading={isLoading}
      list={[
        [
          {
            label: '화주명',
            required: true,
            validationValue: values.shipperNames && values.shipperNames.length > 0,
            value: (
              <ShipperSelector
                isSingleLogisticsServiceType
                logisticsServiceTypesWidth={120}
                shipperNamesWidth={250}
                onlyFullSearchShipperNamesMode={values.logisticsServiceTypes?.[0] === 'MFS'}
                onFieldChange={onFieldChange}
              />
            ),
          },
          {
            label: '주문번호',
            value: (
              <MultipleInput modalTitle="주문번호" value={values.orderNumbers ?? []} onChange={(v) => setValue('orderNumbers', v)} maxSize={200} />
            ),
          },
        ],
      ]}
      onReset={onReset}
      onSearch={handleSubmit(onSubmit)}
    />
  )

  const grid = (
    <GridTable<OrderItem>
      title="예상 할당 목록"
      useColumnNo
      ref={$grid}
      isLoading={isLoading}
      countPerPage={GRID_SIZE.MEDIUM}
      onChangePage={onChangePage}
      totalCount={data?.pageInfo?.totalCount || 0}
      totalPages={data?.pageInfo?.totalPages}
      currentPage={currentPage || 0}
      rowData={data?.items || []}
      columnDefs={columnDefs}
      gridKey="GI23_B2C"
      legacyStorageKey="b2c-outbound-list"
    />
  )

  // fulfillment.optimization.b2c.activeShippers.enabled — 신규 레이아웃:
  // 필터+그리드를 좌측 컬럼으로 묶고, 출고지시 대기 수량 패널을 페이지 우측에 세로 전체로 배치
  if (isFulfillmentEnabled) {
    return (
      <HStack full gap={16}>
        <VStack gap={16} style={{ flex: 1, minWidth: 0 }}>
          {filter}
          {/* GridTableWrap이 height auto라 VStack(column) 안에서 sum이 0이 됨. wrapper로 남는 height를 강제 분배. */}
          <GridFlexFill>{grid}</GridFlexFill>
        </VStack>
        <OutboundWaitingByTimeSlotPanel cutType="B2C" />
      </HStack>
    )
  }

  return (
    <VStack gap={16}>
      {filter}
      <HStack full gap={16}>
        {grid}
        <SummaryPanel item={expectedAllocationData} filteredWarehouses={filteredWarehouses} />
      </HStack>
    </VStack>
  )
}

const GridFlexFill = styled.div`
  flex: 1;
  min-height: 0;
  max-height: 650px;
  display: flex;
  flex-direction: column;

  & > * {
    flex: 1;
    min-height: 0;
  }
`

export default B2CContent
